import type { JSX, Key, ReactNode } from 'react';
import { useMemo } from 'react';
import Markdown from 'react-markdown';
import { Link } from 'react-router-dom';
import remarkGfm from 'remark-gfm';

import { MermaidDiagram } from '~/components/MermaidDiagram';
import { CodeBlock } from '~/pages/Documentation/components/ui/CodeBlock';

import type { PatternDomain } from '~/types';

import { parsePatternDoc } from '../patternXml';
import { stripFrontmatter, stripXmlTags } from '../utils';

import {
  AppliesWhenSection,
  AvoidSection,
  NotesSection,
  RulesSection,
  VerifySection,
} from './PatternStructuredBlocks';

/** Files excluded from pattern manifest — don't rewrite links to these */
const NON_PATTERN_FILES = new Set(['MAINTENANCE.md', 'README.md']);

interface PatternContentProps {
  content: string;
  domain?: PatternDomain;
}

/**
 * Shared inline-markdown renderer, reused for raw `markdown` segments and
 * for the inline markdown body of structural blocks (rule text, mistakes,
 * checks, applies_when/notes bodies) — one renderer, one set of overrides.
 */
function renderMarkdown(body: string, domain?: PatternDomain, key?: Key): JSX.Element {
  return (
    <Markdown
      key={key}
      remarkPlugins={[remarkGfm]}
      components={{
        pre({ children }) {
          return <>{children}</>;
        },
        code({ children, className, ...props }) {
          const match = /language-(\w+)/.exec(className || '');
          const codeString = String(children).replace(/\n$/, '');

          if (match?.[1] === 'mermaid') {
            return <MermaidDiagram chart={codeString} />;
          }

          if (match) {
            return <CodeBlock language={match[1]}>{codeString}</CodeBlock>;
          }

          // Fenced code block without language (ASCII wireframes, plain text)
          if (codeString.includes('\n')) {
            return <CodeBlock>{codeString}</CodeBlock>;
          }

          return (
            <code
              className="px-1.5 py-0.5 rounded bg-surface-elevated text-sm font-mono"
              {...props}
            >
              {children}
            </code>
          );
        },
        h1({ children }) {
          return <h1 className="text-2xl font-bold mt-8 mb-4 text-foreground">{children}</h1>;
        },
        h2({ children }) {
          return (
            <h2 className="text-xl font-semibold mt-6 mb-3 text-foreground border-b border-default pb-2">
              {children}
            </h2>
          );
        },
        h3({ children }) {
          return <h3 className="text-lg font-semibold mt-5 mb-2 text-foreground">{children}</h3>;
        },
        p({ children }) {
          return <p className="mb-4 text-foreground leading-relaxed">{children}</p>;
        },
        ul({ children }) {
          return (
            <ul className="list-disc list-inside mb-4 space-y-1 text-foreground">{children}</ul>
          );
        },
        ol({ children }) {
          return (
            <ol className="list-decimal list-inside mb-4 space-y-1 text-foreground">{children}</ol>
          );
        },
        blockquote({ children }) {
          return (
            <blockquote className="border-l-4 border-primary pl-4 my-4 text-muted italic">
              {children}
            </blockquote>
          );
        },
        table({ children }) {
          return (
            <div className="overflow-x-auto my-4">
              <table className="min-w-full border-collapse border border-default">{children}</table>
            </div>
          );
        },
        thead({ children }) {
          return <thead className="bg-surface-elevated">{children}</thead>;
        },
        th({ children }) {
          return (
            <th className="border border-default px-4 py-2 text-left text-sm font-semibold text-foreground">
              {children}
            </th>
          );
        },
        td({ children }) {
          return (
            <td className="border border-default px-4 py-2 text-sm text-foreground">{children}</td>
          );
        },
        a({ children, href }) {
          if (!href) {
            return <span className="text-primary">{children}</span>;
          }

          const patternRoute = domain ? resolvePatternHref(href, domain) : null;
          if (patternRoute) {
            return (
              <Link to={patternRoute} className="text-primary hover:underline">
                {children}
              </Link>
            );
          }

          const isExternal = href.startsWith('http');
          return (
            <a
              href={href}
              className="text-primary hover:underline"
              target={isExternal ? '_blank' : undefined}
              rel={isExternal ? 'noopener noreferrer' : undefined}
            >
              {children}
            </a>
          );
        },
      }}
    >
      {body}
    </Markdown>
  );
}

/**
 * Resolve a relative .md href to a /patterns/{pattern_id} route.
 *
 * - `./transaction-management.md` (same domain) → `/patterns/{domain}-transaction-management`
 * - `../security/rls-tenant-isolation.md` (cross-domain) → `/patterns/security-rls-tenant-isolation`
 * - External URLs and non-pattern files are returned as-is.
 */
function resolvePatternHref(href: string, currentDomain: PatternDomain): string | null {
  // External links — pass through
  if (href.startsWith('http://') || href.startsWith('https://')) return null;

  // Must be a .md link to be a pattern reference
  if (!href.endsWith('.md')) return null;

  const filename = href.split('/').pop() ?? '';
  if (NON_PATTERN_FILES.has(filename)) return null;

  const stem = filename.replace(/\.md$/, '');

  if (href.startsWith('../')) {
    // Cross-domain: ../security/rls-tenant-isolation.md → security-rls-tenant-isolation
    const parts = href.replace(/^\.\.\//, '').split('/');
    if (parts.length === 2) {
      const domain = parts[0];
      return `/patterns/${domain}-${stem}`;
    }
  } else if (href.startsWith('./') || !href.includes('/')) {
    // Same-domain: ./transaction-management.md → {currentDomain}-transaction-management
    return `/patterns/${currentDomain}-${stem}`;
  }

  return null;
}

export function PatternContent({ content, domain }: PatternContentProps): JSX.Element {
  const segments = useMemo(() => parsePatternDoc(stripFrontmatter(content)), [content]);
  const renderInline = (text: string): ReactNode => renderMarkdown(text, domain);

  return (
    <div className="prose dark:prose-invert max-w-none">
      {segments.map((segment, index) => {
        switch (segment.kind) {
          case 'markdown':
            // Identical to the pre-parser pipeline: strip wrapper tags, render as markdown.
            // Guarantees byte-for-byte unchanged output for frameworks, ref-archs, and examples.
            return renderMarkdown(stripXmlTags(segment.body), domain, index);
          case 'rules':
            return <RulesSection key={index} renderMarkdown={renderInline} rules={segment.rules} />;
          case 'applies_when':
            return (
              <AppliesWhenSection key={index} body={segment.body} renderMarkdown={renderInline} />
            );
          case 'avoid':
            return (
              <AvoidSection key={index} mistakes={segment.mistakes} renderMarkdown={renderInline} />
            );
          case 'verify':
            return (
              <VerifySection key={index} checks={segment.checks} renderMarkdown={renderInline} />
            );
          case 'notes':
            return <NotesSection key={index} body={segment.body} renderMarkdown={renderInline} />;
        }
      })}
    </div>
  );
}
